import { Zap, describeError, NOT_CONFIGURED, OTP_REQUIRED, PIN_REQUIRED, INCORRECT_PIN } from '@theme/brickline-zap';

function message(root, text, tone = 'info') {
  const el = root.querySelector('[data-zap-message]');
  if (!el) return;
  el.textContent = text;
  el.dataset.tone = tone;
  el.hidden = !text;
}

function busy(form, isBusy) {
  const button = form?.querySelector('button[type="submit"]');
  if (button) button.disabled = isBusy;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** ZAP returns fractional points (5000.20); trim the decimal when it's whole. */
function formatPoints(value) {
  const n = Number(value || 0);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });
}

/**
 * GET_BALANCE's auth mode requires a PIN on every single /balance/inquiry
 * call — that's stateless on ZAP's side, not something the endpoint can cache
 * server-side. Without remembering it here, a shopper would have to retype
 * their PIN on every page load or refresh just to see a number they already
 * unlocked once. sessionStorage only: cleared the moment the tab closes,
 * never touches localStorage, and is wiped immediately if ZAP ever reports
 * the cached PIN as wrong.
 */
const BALANCE_PIN_KEY = 'brickline_zap_balance_pin';

function getCachedBalancePin() {
  try {
    return sessionStorage.getItem(BALANCE_PIN_KEY) || '';
  } catch {
    return '';
  }
}

function setCachedBalancePin(pin) {
  try {
    sessionStorage.setItem(BALANCE_PIN_KEY, pin);
  } catch {
    /* private browsing / storage disabled — just skip caching */
  }
}

function clearCachedBalancePin() {
  try {
    sessionStorage.removeItem(BALANCE_PIN_KEY);
  } catch {
    /* noop */
  }
}

/* ---------------------------------------------------------------- enrolment */

class ZapEnrol extends HTMLElement {
  connectedCallback() {
    this.mobileForm = this.querySelector('[data-zap-mobile-form]');
    this.enrolOtpForm = this.querySelector('[data-zap-enrol-otp-form]');
    this.enrolForm = this.querySelector('[data-zap-enrol-form]');
    this.otpForm = this.querySelector('[data-zap-otp-form]');
    this.linkForm = this.querySelector('[data-zap-link-form]');
    this.tabs = [...this.querySelectorAll('[data-zap-tab]')];
    this.panels = {
      new: this.querySelector('[data-zap-panel="new"]'),
      existing: this.querySelector('[data-zap-panel="existing"]'),
    };
    this.mobileForm?.addEventListener('submit', this.#onStartEnrol);
    this.enrolOtpForm?.addEventListener('submit', this.#onEnrolOtp);
    this.enrolForm?.addEventListener('submit', this.#onEnrol);
    this.otpForm?.addEventListener('submit', this.#onVerify);
    this.linkForm?.addEventListener('submit', this.#onLink);
    for (const tab of this.tabs) tab.addEventListener('click', this.#onTabClick);
  }

  /** Set by #onStartEnrol; carried through to the final #onEnrol submit. */
  #reference = null;
  /** Set by #onEnrolOtp; the endpoint doesn't confirm it until Register has run. */
  #otp = null;
  /** Set by #onLink — #onVerify confirms it directly, via Zap.verifyLink. */
  #linkReference = null;

  /**
   * Switches between the "new member" (/enrol) and "existing member" (/link)
   * flows. Each panel resets to its first step on switch, so leaving one
   * mid-flow and coming back always starts clean rather than showing a stale
   * OTP/profile form tied to a reference the shopper may no longer have typed
   * a matching code for.
   */
  #onTabClick = (event) => {
    const target = event.currentTarget.dataset.zapTab;

    for (const tab of this.tabs) {
      const active = tab.dataset.zapTab === target;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    }
    for (const [name, panel] of Object.entries(this.panels)) {
      if (panel) panel.hidden = name !== target;
    }

    this.#reference = null;
    this.#otp = null;
    this.#linkReference = null;
    this.mobileForm.hidden = false;
    this.enrolOtpForm.hidden = true;
    this.enrolForm.hidden = true;
    this.linkForm.hidden = false;
    this.otpForm.hidden = true;
    message(this, '');
  };

  /**
   * A shopper whose ZAP account predates this store's move to Shopify already
   * has a mobile number ZAP knows — registering again just gets 400-04. This
   * sends an OTP to prove they own that number, then /link/verify links it to
   * this Shopify customer without creating a second ZAP account.
   */
  #onLink = async (event) => {
    event.preventDefault();
    const mobile = String(new FormData(this.linkForm).get('mobile') || '').trim();
    if (!mobile) return;

    busy(this.linkForm, true);
    message(this, 'Sending you a verification code…');

    const result = await Zap.linkExisting(mobile);
    busy(this.linkForm, false);

    if (result.error === OTP_REQUIRED) {
      this.#linkReference = result.reference;
      this.linkForm.hidden = true;
      this.otpForm.hidden = false;
      this.otpForm.querySelector('input')?.focus();
      message(this, 'We sent a code to your mobile. Enter it to link your existing account.');
      return;
    }

    message(this, describeError(result.error), 'error');
  };

  /**
   * Step 1: mobile number only. Sends the OTP before anything else is asked
   * for, per ZAP's own "Sample Flows" diagram — Register doesn't run yet.
   */
  #onStartEnrol = async (event) => {
    event.preventDefault();
    const mobile = String(new FormData(this.mobileForm).get('mobile') || '').trim();
    if (!mobile) return;

    busy(this.mobileForm, true);
    message(this, 'Sending you a verification code…');

    const result = await Zap.startEnrol(mobile);
    busy(this.mobileForm, false);

    if (result.error === OTP_REQUIRED) {
      this.#reference = result.reference;
      this.mobileForm.hidden = true;
      this.enrolOtpForm.hidden = false;
      this.enrolOtpForm.querySelector('input')?.focus();
      message(this, 'We sent a code to your mobile. Enter it to continue.');
      return;
    }

    message(this, describeError(result.error), 'error');
  };

  /**
   * Step 2: hold the code — ZAP has nothing to check it against yet, since
   * Register hasn't run. #onEnrol spends it once the account exists.
   */
  #onEnrolOtp = (event) => {
    event.preventDefault();
    const otp = String(new FormData(this.enrolOtpForm).get('otp') || '').trim();
    if (!otp) return;

    this.#otp = otp;
    this.enrolOtpForm.hidden = true;
    this.enrolForm.hidden = false;
    this.enrolForm.querySelector('input')?.focus();
    message(this, 'Finish setting up your account.');
  };

  /**
   * Step 3: the rest of the profile plus PIN. This is what actually calls
   * Register, then spends step 2's OTP via Update Membership. If the code was
   * wrong, this is also where that error surfaces — go back and get a new
   * code (the enrol/link toggle stays hidden mid-flow, but the ZAP account,
   * if Register already succeeded here, will just need /link next time).
   */
  #onEnrol = async (event) => {
    event.preventDefault();
    const data = new FormData(this.enrolForm);
    const firstName = String(data.get('firstName') || '').trim();
    const lastName = String(data.get('lastName') || '').trim();
    const email = String(data.get('email') || '').trim();
    const birthday = String(data.get('birthday') || '').trim();
    const city = String(data.get('city') || '').trim();
    const gender = String(data.get('gender') || '').trim();
    const pin = String(data.get('pin') || '').trim();

    if (!firstName || !lastName || !email || !birthday || !city || !gender || !pin) {
      message(this, 'Please fill in every field.', 'error');
      return;
    }

    busy(this.enrolForm, true);
    message(this, 'Registering you with the rewards programme…');

    // The endpoint adds branchId and mobile (from step 1's reference); this
    // merchant's config marks gender and birthday required (plus
    // firstName/lastName/email/city), or ZAP answers 400-14. pin sets the
    // shopper's PIN for later Pin/PinOtp calls.
    const result = await Zap.enrol({
      reference: this.#reference,
      otp: this.#otp,
      firstName,
      lastName,
      email,
      birthday,
      city,
      gender,
      pin,
    });
    busy(this.enrolForm, false);

    if (result.ok) {
      message(this, 'You’re in. Loading your points…', 'success');
      location.reload();
      return;
    }

    message(this, describeError(result.error), 'error');
  };

  #onVerify = async (event) => {
    event.preventDefault();
    const otp = String(new FormData(this.otpForm).get('otp') || '').trim();
    if (!otp) return;

    busy(this.otpForm, true);
    const result = await Zap.verifyLink(this.#linkReference, otp);
    busy(this.otpForm, false);

    if (!result.ok) {
      message(this, describeError(result.error), 'error');
      return;
    }

    // The endpoint has written the metafields; reload so Liquid renders the
    // member state rather than reconstructing it here.
    message(this, 'You’re in. Loading your points…', 'success');
    location.reload();
  };
}

/* ---------------------------------------------------------------- dashboard */

class ZapDashboard extends HTMLElement {
  connectedCallback() {
    this.currencies = this.querySelector('[data-zap-currencies]');
    this.tier = this.querySelector('[data-zap-tier]');
    this.balanceAuthWrap = this.querySelector('[data-zap-balance-auth]');
    this.balancePinStep = this.querySelector('[data-zap-balance-pin-step]');
    this.redeemForm = this.querySelector('[data-zap-redeem-form]');
    this.redeemSendStep = this.querySelector('[data-zap-redeem-send-step]');
    this.redeemSendButton = this.querySelector('[data-zap-redeem-send]');
    this.redeemOtpField = this.querySelector('[data-zap-redeem-otp]');
    this.redeemPinField = this.querySelector('[data-zap-redeem-pin]');
    this.redeemActions = this.querySelector('[data-zap-redeem-actions]');
    this.couponsRoot = this.querySelector('[data-zap-coupons]');
    this.couponAuthWrap = this.querySelector('[data-zap-coupon-auth]');
    this.couponSendStep = this.querySelector('[data-zap-coupon-send-step]');
    this.couponSendButton = this.querySelector('[data-zap-coupon-send]');
    this.couponOtpStep = this.querySelector('[data-zap-coupon-otp-step]');
    this.transactionsRoot = this.querySelector('[data-zap-transactions]');
    this.moreButton = this.querySelector('[data-zap-more]');

    this.balancePinStep?.addEventListener('submit', this.#onBalancePinSubmit);
    this.redeemForm?.addEventListener('submit', this.#onRedeemPoints);
    this.redeemSendButton?.addEventListener('click', this.#onRedeemSend);
    this.couponsRoot?.addEventListener('click', this.#onRedeemCoupon);
    this.couponSendButton?.addEventListener('click', this.#onCouponSend);
    this.couponOtpStep?.addEventListener('submit', this.#onCouponOtpSubmit);
    this.moreButton?.addEventListener('click', () => this.#loadTransactions(this.#marker));

    this.#loadBalance();
    this.#loadTier();
    if (this.couponsRoot) this.#loadCoupons();
    if (this.transactionsRoot) this.#loadTransactions();
  }

  #validPoints = 0;
  #currency = null;
  #marker = null;
  #redeemReference = null;
  /** Set once the balance panel's PIN step succeeds; reused by the redeem
   *  flow's own balance re-check below, which needs the same PIN — see
   *  #onRedeemPoints. Seeded from sessionStorage so a page refresh doesn't
   *  force the shopper to retype a PIN they already verified this tab
   *  session — see #loadBalance, which is what actually persists it. */
  #balancePin = getCachedBalancePin();

  /** Shows the balance panel's PIN step, or hides it. */
  #showBalanceStep(step) {
    if (this.balanceAuthWrap) this.balanceAuthWrap.hidden = !step;
    if (this.balancePinStep) this.balancePinStep.hidden = step !== 'pin';
  }

  async #loadBalance(auth, { preserveMessage = false } = {}) {
    // A PIN typed this call wins; otherwise fall back to one already verified
    // this tab session, so a plain page refresh doesn't re-prompt for it.
    const pin = auth?.pin || this.#balancePin || undefined;
    const result = await Zap.balance({ ...auth, pin });

    // GET_BALANCE's auth mode for this merchant is PIN-only (confirmed with
    // ZAP support) — unlike redeem below, it never asks for an OTP.
    if (result.error === PIN_REQUIRED) {
      this.#showBalanceStep('pin');
      message(this, 'Enter your PIN to see your points.');
      return;
    }

    if (result.error === INCORRECT_PIN) {
      // A cached PIN from an earlier page load stopped working (changed,
      // expired, etc.) — drop it rather than retry it silently forever.
      this.#balancePin = '';
      clearCachedBalancePin();
      this.#showBalanceStep('pin');
      message(this, describeError(result.error), 'error');
      return;
    }

    if (!result.ok) {
      // A missing proxy isn't the shopper's fault — say so and leave the
      // placeholders rather than rendering a fake zero.
      message(this, describeError(result.error), result.error === NOT_CONFIGURED ? 'info' : 'error');
      this.#disableActions();
      return;
    }

    // The PIN just used (typed or cached) was confirmed correct — remember
    // it so the next #loadBalance call, including one from a fresh page
    // load, doesn't have to ask again.
    if (pin) {
      this.#balancePin = pin;
      setCachedBalancePin(pin);
    }

    this.#showBalanceStep(null);
    // A refresh triggered by a redemption must not wipe the confirmation the
    // shopper just earned — only clear prompts this method put up itself.
    if (!preserveMessage) message(this, '');

    const currencies = (result.currencies || []).slice().sort((a, b) => (b.priority || 0) - (a.priority || 0));
    if (currencies.length === 0) return;

    // Redemptions are validated against the currency shown first, so remember
    // which one that is and tell the endpoint — ZAP's array order is not the
    // display order.
    this.#currency = currencies[0];
    this.#validPoints = Number(currencies[0].validPoints || 0);
    this.currencies.replaceChildren();

    currencies.forEach((currency, index) => {
      const wrap = el('div', index === 0 ? 'rewards__points' : 'rewards__points rewards__points--muted');
      wrap.appendChild(el('span', 'rewards__points-value', formatPoints(currency.validPoints)));
      wrap.appendChild(el('span', 'rewards__points-label', currency.name || 'Points'));

      if (Number(currency.pendingPoints) > 0) {
        wrap.appendChild(
          el('span', 'rewards__points-pending', `${formatPoints(currency.pendingPoints)} pending`)
        );
      }
      this.currencies.appendChild(wrap);
    });
  }

  /** The balance panel's only step: the shopper's PIN. */
  #onBalancePinSubmit = async (event) => {
    event.preventDefault();
    const pin = String(new FormData(this.balancePinStep).get('pin') || '').trim();
    if (!pin) return;

    busy(this.balancePinStep, true);
    await this.#loadBalance({ pin });
    busy(this.balancePinStep, false);
  };

  async #loadTier() {
    const result = await Zap.membership();
    if (!result.ok || !result.rank?.name || !this.tier) return;
    this.tier.textContent = result.rank.name;
    this.tier.hidden = false;
  }

  async #loadCoupons() {
    const result = await Zap.coupons();

    if (!result.ok) {
      this.couponsRoot.replaceChildren(el('p', 'account__empty', describeError(result.error)));
      return;
    }

    const coupons = result.coupons || [];
    if (coupons.length === 0) {
      this.couponsRoot.replaceChildren(el('p', 'account__empty', 'No coupons available right now.'));
      return;
    }

    const list = el('ul', 'rewards__coupons');

    for (const coupon of coupons) {
      const item = el('li', 'rewards__coupon');
      const body = el('div');

      body.appendChild(el('span', 'rewards__coupon-name', coupon.promotion?.name || `Coupon ${coupon.id}`));
      if (coupon.promotion?.description) {
        body.appendChild(el('span', 'rewards__coupon-validity', coupon.promotion.description));
      }
      if (coupon.validityEndDate) {
        body.appendChild(
          el(
            'span',
            'rewards__coupon-validity',
            coupon.validityStartDate
              ? `Valid ${formatDate(coupon.validityStartDate)} – ${formatDate(coupon.validityEndDate)}`
              : `Valid until ${formatDate(coupon.validityEndDate)}`
          )
        );
      }
      item.appendChild(body);

      // Only Available + active coupons can be redeemed; Used and Expired are
      // shown so the shopper can see their history, but not offered.
      if (coupon.status === 'Available' && coupon.isActive) {
        const button = el('button', 'account__button account__button--secondary', 'Use coupon');
        button.type = 'button';
        button.dataset.zapRedeemCoupon = coupon.id;
        item.appendChild(button);
      } else {
        item.appendChild(el('span', 'rewards__coupon-status', coupon.status || 'Unavailable'));
      }

      list.appendChild(item);
    }

    this.couponsRoot.replaceChildren(list);
  }

  async #loadTransactions(marker) {
    const result = await Zap.transactions(marker);

    if (!result.ok) {
      if (!marker) {
        this.transactionsRoot.replaceChildren(el('p', 'account__empty', describeError(result.error)));
      }
      if (this.moreButton) this.moreButton.hidden = true;
      return;
    }

    const transactions = result.transactions || [];
    if (!marker && transactions.length === 0) {
      this.transactionsRoot.replaceChildren(el('p', 'account__empty', 'No activity yet.'));
      if (this.moreButton) this.moreButton.hidden = true;
      return;
    }

    const list = marker ? this.transactionsRoot.querySelector('.rewards__activity') : el('ul', 'rewards__activity');
    if (!marker) this.transactionsRoot.replaceChildren(list);

    for (const tx of transactions) {
      const item = el('li', 'rewards__activity-row');
      const body = el('div');

      const earned = (tx.points || []).reduce((sum, p) => sum + Number(p.earned || 0), 0);
      const redeemed = (tx.points || []).reduce((sum, p) => sum + Number(p.redeemed || 0), 0);
      const currency = tx.points?.[0]?.currency || 'points';

      let label = 'Transaction';
      if (earned > 0) label = `Earned ${formatPoints(earned)} ${currency}`;
      else if (redeemed > 0) label = `Redeemed ${formatPoints(redeemed)} ${currency}`;
      else if ((tx.coupons || []).length) label = tx.coupons[0].earned ? 'Coupon earned' : 'Coupon used';

      body.appendChild(el('span', 'rewards__activity-label', label));
      body.appendChild(
        el(
          'span',
          'rewards__activity-meta',
          [formatDate(tx.dateProcessed), tx.branchName || tx.merchantName, tx.refNo].filter(Boolean).join(' · ')
        )
      );
      item.appendChild(body);

      // Pending points haven't cleared in ZAP yet, so flag them.
      if (tx.status && tx.status !== 'Cleared') {
        item.appendChild(el('span', 'rewards__activity-status', tx.status));
      }
      list.appendChild(item);
    }

    this.#marker = result.marker || null;
    if (this.moreButton) this.moreButton.hidden = !this.#marker || transactions.length === 0;
  }

  #onRedeemPoints = async (event) => {
    event.preventDefault();
    const data = new FormData(this.redeemForm);
    const amount = Number(data.get('amount'));
    const otp = String(data.get('otp') || '').trim();
    const pin = String(data.get('pin') || '').trim();

    if (!Number.isFinite(amount) || amount < 1) {
      message(this, 'Enter how many points you want to redeem.', 'error');
      return;
    }

    // Courtesy check only — the endpoint re-reads the real balance from ZAP
    // before honouring anything, because this figure came from the browser.
    if (this.#validPoints && amount > this.#validPoints) {
      const name = this.#currency?.name || 'points';
      message(this, `You only have ${formatPoints(this.#validPoints)} ${name} available.`, 'error');
      return;
    }

    busy(this.redeemForm, true);
    // Reuses whatever PIN the balance panel already collected (the redeem
    // endpoint's own balance re-check needs the same one) — the redeem
    // form's own PIN field is only a fallback for whichever wasn't typed.
    const result = await Zap.redeemPoints(amount, {
      otp,
      pin: pin || this.#balancePin || undefined,
      reference: this.#redeemReference,
      currencyId: this.#currency?.id,
    });
    busy(this.redeemForm, false);

    if (result.error === PIN_REQUIRED) {
      if (this.redeemPinField) this.redeemPinField.hidden = false;
      if (this.redeemActions) this.redeemActions.hidden = false;
      this.redeemPinField?.querySelector('input')?.focus();
      message(this, 'Enter your PIN to confirm this redemption.');
      return;
    }

    if (result.error === OTP_REQUIRED) {
      // Not yet sent — the redeem endpoint only detects the requirement now,
      // same as /balance. #onRedeemSend below does the actual sending, so
      // the shopper sees why before an SMS goes out rather than an empty
      // code field appearing with no context.
      if (this.redeemSendStep) this.redeemSendStep.hidden = false;
      if (this.redeemActions) this.redeemActions.hidden = true;
      message(this, '');
      return;
    }

    if (!result.ok) {
      message(this, describeError(result.error), 'error');
      return;
    }

    message(this, `Redeemed. Reference ${result.transaction_ref}.`, 'success');
    this.redeemForm.reset();
    this.#redeemReference = null;
    if (this.redeemSendStep) this.redeemSendStep.hidden = true;
    if (this.redeemOtpField) this.redeemOtpField.hidden = true;
    if (this.redeemPinField) this.redeemPinField.hidden = true;
    if (this.redeemActions) this.redeemActions.hidden = false;
    this.#loadBalance(undefined, { preserveMessage: true });
    if (this.transactionsRoot) this.#loadTransactions();
  };

  /** Redeem's "send code" step. Not sent automatically: the shopper sees why
   *  a code is going out before an SMS fires, rather than an empty code
   *  field appearing with no context. */
  #onRedeemSend = async () => {
    if (this.redeemSendButton) this.redeemSendButton.disabled = true;
    const result = await Zap.sendAuthOtp('redeem');
    if (this.redeemSendButton) this.redeemSendButton.disabled = false;

    if (result.error === OTP_REQUIRED) {
      this.#redeemReference = result.reference;
      if (this.redeemSendStep) this.redeemSendStep.hidden = true;
      if (this.redeemOtpField) this.redeemOtpField.hidden = false;
      if (this.redeemActions) this.redeemActions.hidden = false;
      this.redeemOtpField?.querySelector('input')?.focus();
      message(this, 'We sent a code to your mobile. Enter it to confirm this redemption.');
      return;
    }

    message(this, describeError(result.error), 'error');
  };

  /** Which coupon a send/OTP step in progress is for — only one redemption
   *  can be mid-flight at a time, so this plus #couponReference is enough,
   *  same pattern as #balanceReference was for the balance panel. */
  #pendingCouponId = null;
  #couponReference = null;

  /** Shows the coupon panel's send/OTP step, or hides it. */
  #showCouponStep(step) {
    if (this.couponAuthWrap) this.couponAuthWrap.hidden = !step;
    if (this.couponSendStep) this.couponSendStep.hidden = step !== 'send';
    if (this.couponOtpStep) this.couponOtpStep.hidden = step !== 'otp';
  }

  #onRedeemCoupon = async (event) => {
    const button = event.target.closest('[data-zap-redeem-coupon]');
    if (!button) return;

    const couponId = button.dataset.zapRedeemCoupon;
    button.disabled = true;
    const result = await Zap.redeemCoupon(couponId);
    button.disabled = false;

    // Coupon redemption is gated by the same USE_POINTS auth mode as
    // Redeem points — not yet confirmed whether this merchant actually
    // requires it here too, but the endpoint passes otp_required through if
    // ZAP says so, same as it does for points.
    if (result.error === OTP_REQUIRED) {
      this.#pendingCouponId = couponId;
      this.#showCouponStep('send');
      message(this, '');
      return;
    }

    if (!result.ok) {
      message(this, describeError(result.error), 'error');
      return;
    }

    this.#finishCouponRedeem(result);
  };

  /** Step 1 (only shown when a code is actually needed): sends it. Reuses
   *  the same 'redeem' purpose as the points-redeem form's send step — it's
   *  the same USE_POINTS gate on ZAP's side regardless of which flow asked. */
  #onCouponSend = async () => {
    if (this.couponSendButton) this.couponSendButton.disabled = true;
    const result = await Zap.sendAuthOtp('redeem');
    if (this.couponSendButton) this.couponSendButton.disabled = false;

    if (result.error === OTP_REQUIRED) {
      this.#couponReference = result.reference;
      this.#showCouponStep('otp');
      message(this, 'We sent a code to your mobile. Enter it to confirm this redemption.');
      return;
    }

    message(this, describeError(result.error), 'error');
  };

  /** Step 2: the code from #onCouponSend, resubmitting the same coupon. */
  #onCouponOtpSubmit = async (event) => {
    event.preventDefault();
    const otp = String(new FormData(this.couponOtpStep).get('otp') || '').trim();
    if (!otp || !this.#pendingCouponId) return;

    busy(this.couponOtpStep, true);
    const result = await Zap.redeemCoupon(this.#pendingCouponId, { otp, reference: this.#couponReference });
    busy(this.couponOtpStep, false);

    if (!result.ok) {
      message(this, describeError(result.error), 'error');
      return;
    }

    this.#pendingCouponId = null;
    this.#couponReference = null;
    this.#showCouponStep(null);
    this.#finishCouponRedeem(result);
  };

  /** Shared success path for both the no-OTP-needed and OTP-confirmed cases
   *  above. discount_code is null when this promotion has no
   *  COUPON_DISCOUNT_MAP entry on the endpoint (see
   *  docs/zap-proxy-stub/worker.js) — the coupon is still spent on ZAP's
   *  side either way, so say so rather than leaving the shopper thinking
   *  nothing happened. */
  #finishCouponRedeem(result) {
    message(
      this,
      result.discount_code
        ? `Redeemed! Use code ${result.discount_code} at checkout.`
        : `Redeemed. Reference ${result.transaction_ref}. Contact support to claim your discount.`,
      'success'
    );
    this.#loadCoupons();
    if (this.transactionsRoot) this.#loadTransactions();
  }

  #disableActions() {
    for (const control of this.querySelectorAll('button')) control.disabled = true;
  }
}

if (!customElements.get('zap-enrol')) customElements.define('zap-enrol', ZapEnrol);
if (!customElements.get('zap-dashboard')) customElements.define('zap-dashboard', ZapDashboard);
